/** Modelos de projeto: arquivos iniciais criados junto com o projeto. */
/** "import": projeto criado a partir de uma pasta do computador (sem arquivos iniciais; não aparece na lista). */
export const TEMPLATES = ["node", "typescript", "python", "java", "web", "blank", "import"] as const;
export type TemplateId = (typeof TEMPLATES)[number];

export interface TemplateFile {
  path: string;
  content: string;
}

export interface TemplateDefinition {
  id: TemplateId;
  label: string;
  description: string;
  runtime: "node" | "python" | "java";
  files: TemplateFile[];
}

const README = (title: string, run: string) => `# ${title}

Projeto criado no Kompil. Abra o terminal (Ctrl+\`) e rode:

\`\`\`bash
${run}
\`\`\`

O código é compartilhado com quem você convidar; o terminal é só seu.
`;

export const TEMPLATE_DEFINITIONS: Record<TemplateId, TemplateDefinition> = {
  node: {
    id: "node",
    label: "Node.js",
    description: "JavaScript no servidor com um servidor HTTP mínimo.",
    runtime: "node",
    files: [
      {
        path: "package.json",
        content: `{
  "name": "app",
  "version": "1.0.0",
  "type": "module",
  "scripts": {
    "start": "node src/index.js",
    "dev": "node --watch src/index.js"
  }
}
`,
      },
      {
        path: "src/index.js",
        content: `import { createServer } from "node:http";

const port = Number(process.env.PORT ?? 3000);

const server = createServer((req, res) => {
  res.writeHead(200, { "content-type": "application/json" });
  res.end(JSON.stringify({ ok: true, path: req.url }));
});

server.listen(port, () => {
  console.log(\`Servidor ouvindo em http://localhost:\${port}\`);
});
`,
      },
      { path: "README.md", content: README("Node.js", "npm start") },
    ],
  },
  typescript: {
    id: "typescript",
    label: "TypeScript",
    description: "TypeScript executado direto pelo Node (type stripping).",
    runtime: "node",
    files: [
      {
        path: "package.json",
        content: `{
  "name": "app",
  "version": "1.0.0",
  "type": "module",
  "scripts": {
    "start": "node src/main.ts"
  }
}
`,
      },
      {
        path: "src/main.ts",
        content: `interface Dev {
  name: string;
  skills: string[];
}

const team: Dev[] = [
  { name: "Ana", skills: ["typescript", "react"] },
  { name: "Bia", skills: ["python", "sql"] },
];

for (const dev of team) {
  console.log(\`\${dev.name}: \${dev.skills.join(", ")}\`);
}
`,
      },
      { path: "README.md", content: README("TypeScript", "npm start") },
    ],
  },
  python: {
    id: "python",
    label: "Python",
    description: "Script Python 3 pronto para rodar.",
    runtime: "python",
    files: [
      {
        path: "main.py",
        content: `def greet(name: str) -> str:
    return f"Olá, {name}!"


if __name__ == "__main__":
    print(greet("mundo"))
`,
      },
      { path: "requirements.txt", content: "" },
      { path: "README.md", content: README("Python", "python3 main.py") },
    ],
  },
  java: {
    id: "java",
    label: "Java",
    description: "Java 17 com compilação e execução pelo terminal (javac + java).",
    runtime: "java",
    files: [
      {
        path: "src/Main.java",
        content: `public class Main {
    static String greet(String name) {
        return "Olá, " + name + "!";
    }

    public static void main(String[] args) {
        System.out.println(greet("mundo"));
    }
}
`,
      },
      {
        path: ".gitignore",
        content: "out/\n*.class\n",
      },
      { path: "README.md", content: README("Java", "javac -d out src/*.java && java -cp out Main") },
    ],
  },
  web: {
    id: "web",
    label: "Web estático",
    description: "HTML, CSS e JavaScript servidos localmente.",
    runtime: "node",
    files: [
      {
        path: "index.html",
        content: `<!doctype html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>App</title>
    <link rel="stylesheet" href="style.css" />
  </head>
  <body>
    <main>
      <h1>Olá!</h1>
      <button id="btn">Clique</button>
    </main>
    <script src="script.js"></script>
  </body>
</html>
`,
      },
      {
        path: "style.css",
        content: `body {
  font-family: system-ui, sans-serif;
  display: grid;
  place-items: center;
  min-height: 100vh;
  margin: 0;
}
`,
      },
      {
        path: "script.js",
        content: `let clicks = 0;
document.getElementById("btn").addEventListener("click", () => {
  clicks += 1;
  document.querySelector("h1").textContent = \`Cliques: \${clicks}\`;
});
`,
      },
      { path: "README.md", content: README("Web estático", "npx serve .") },
    ],
  },
  blank: {
    id: "blank",
    label: "Em branco",
    description: "Só um README. Você decide o resto.",
    runtime: "node",
    files: [{ path: "README.md", content: "# Novo projeto\n" }],
  },
  import: {
    id: "import",
    label: "Pasta importada",
    description: "Os arquivos vêm da pasta escolhida no computador.",
    runtime: "node",
    files: [],
  },
};
